/* istanbul ignore file */
import React, { memo } from 'react';
import VacancyLinkField from 'domain/recruiting/vacancy/shared/input/VacancyLinkField';
import CandidateApplicationSourceField from 'domain/recruiting/candidate/shared/field/CandidateApplicationSourceField';
import CandidateSearchLinkField from 'domain/recruiting/candidate/shared/field/CandidateSearchLinkField';
import ApplicationStageField from 'domain/recruiting/applications/shared/field/ApplicationStageField';
import { DateField, TextField, BadgeField } from 'framework/ui/field';
import CandidateApplicationStatusBadge from 'domain/recruiting/candidate/applications/shared/badge/CandidateApplicationStatusBadge';
import ApplicationCandidateScoreBadge from 'domain/recruiting/applications/shared/badge/ApplicationCandidateScoreBadge';
import CandidateApplicationSourceTypeField from 'domain/recruiting/candidate/applications/shared/field/CandidateApplicationSourceTypeField';
import SetApplicationStatusButton from 'domain/recruiting/candidate/applications/status/SetApplicationStatusButton';
import { useAuthorization } from 'framework/authorization';
import { GROUP_APPLICATION, CHANGE_APPLICATION_STATUS } from 'domain/recruiting/applications/shared/applicationService';
import ProgressField from 'domain/recruiting/applications/shared/field/ProgressField';
import TimeInProgressField from 'domain/recruiting/applications/shared/field/TimeInProgressField';
import LastTouchApplicationField from 'domain/recruiting/applications/shared/field/LastTouchApplicationField';
import { CandidateApplicationStatus } from 'domain/recruiting/candidate/applications/shared/candidateApplicationService';
import { EmployeeLinkField } from 'domain/shared/employee';
import { DataGridLayout, RowWithDetailsPanel } from 'framework/list';

const DAYS_COLOR_BORDER = {
  greenBorder: 1.5,
  yellowBorder: 3.5,
  lightRedBorder: 7.5,
};

const ApplicationsDataGrid = ({
  showTotal,
  hasStageField = true,
  isSmallTable,
}: {
  showTotal?: boolean;
  hasStageField?: boolean;
  isSmallTable?: boolean;
}) => {
  const { isGranted } = useAuthorization();
  const canGroupApplications = isGranted(GROUP_APPLICATION);

  return (
    <DataGridLayout
      isSmallTable
      showTotal={showTotal}
      tableRowComponent={RowWithDetailsPanel}
      sxTableWrapper={{
        '& table': {
          '& th': {
            whiteSpace: 'normal',
            paddingLeft: '10px',
            paddingRight: '10px',
            '&:first-of-type': { width: 'auto' },
            '&:last-child': { width: '90px' },
          },
          '& tbody td': {
            paddingLeft: '10px',
            paddingRight: '10px',
          },
          '& td:first-of-type': { paddingLeft: '3px', paddingRight: 0 },
        },
      }}
    >
      {
        [
          canGroupApplications ? <ProgressField source="progress" label=" " isDetailsPanel width={40} /> : null,
          <TextField source="candidateApplication.id" label="ID" isSortable />,
          <CandidateSearchLinkField
            idSource="candidateApplication.candidate.id"
            source="candidateApplication.candidate.name"
            label="Candidate"
          />,
          <VacancyLinkField
            source="candidateApplication.vacancyAlias"
            nameSource="candidateApplication.vacancyName"
            label="Vacancy"
            getLinkText={({ id, name }: { id: string; name?: string }) => `${id} ${name}`}
          />,
          canGroupApplications ? (
            <BadgeField
              badges={[<ApplicationCandidateScoreBadge source="score" label="Match score" isSortable />]}
              label="Match score"
            />
          ) : null,
          <BadgeField
            badges={[
              <CandidateApplicationStatusBadge source="candidateApplication.status" label="Status" isSortable />,
            ]}
            label="Status"
            width={95}
          />,
          <DateField source="candidateApplication.createdAt" label="Created at" isSortable />,
          <EmployeeLinkField
            source="candidateApplication.createdById"
            nameSource="candidateApplication.createdByName"
            label="Author"
            isSortable
          />,
          <TimeInProgressField
            elapsedTimeSource="candidateApplication.createdAt"
            completedAtSource="completedAt"
            label="Time to hire"
          />,
          <LastTouchApplicationField
            label="Last touch"
            source="candidateApplication.lastTouchAt"
            statusSource="candidateApplication.status"
            colorBorders={DAYS_COLOR_BORDER}
            skipStates={[CandidateApplicationStatus.PROCESSED]}
          />,
          <DateField source="completedAt" label="Completed at" isSortable />,
          <CandidateApplicationSourceTypeField
            source="candidateApplication.sourceType"
            label="Source type"
            isSortable
          />,
          <CandidateApplicationSourceField source="candidateApplication.source" isSortable />,
          hasStageField && canGroupApplications ? (
            <ApplicationStageField source="stage" label="Stage" width={160} />
          ) : null,
          isGranted(CHANGE_APPLICATION_STATUS) ? (
            <SetApplicationStatusButton
              candidateIdSource="candidateApplication.candidate.id"
              candidateApplicationIdSource="candidateApplication.id"
              statusSource="candidateApplication.status"
              sx={isSmallTable ? { height: '2em', whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap' }}
            />
          ) : null,
        ].filter(Boolean) as React.ReactElement[]
      }
    </DataGridLayout>
  );
};

export default memo(ApplicationsDataGrid);
